import { useEffect } from "react";
import { t } from "@/i18n-js/instance";
import type { Appearance } from "@circle-react-shared/BuilderUI/Store/interfaceSlice";
import { LoaderRow } from "@circle-react-shared/LoaderRow";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Modal } from "@circle-react-uikit/ModalV2";
import type { SiteTheme } from "../../constants";
import { useGetThemeStats } from "../../useGetThemeStats";
import { useGetThemes } from "../../useGetThemes";
import { ThemeSelector } from "./ThemeSelector";
import { ThemeUsageStats } from "./ThemeUsageStats";

interface DeleteCustomThemeModalBodyProps {
  appearance: Appearance;
  currentCanvasTheme: SiteTheme;
  newThemeId: number | null;
  setNewThemeId: (themeId: number) => void;
}

export const DeleteCustomThemeModalBody = ({
  appearance,
  currentCanvasTheme,
  newThemeId,
  setNewThemeId,
}: DeleteCustomThemeModalBodyProps) => {
  const { data: themeStats, isLoading: isLoadingThemeStats } = useGetThemeStats(
    currentCanvasTheme.value,
  );
  const { data: themes, isLoading: isLoadingThemes } = useGetThemes();

  const isLoading = isLoadingThemeStats || isLoadingThemes;
  const isThemeInUse = themeStats && themeStats.total_count > 0;

  useEffect(() => {
    if (isThemeInUse && themes) {
      const defaultTheme = themes.find(theme => theme.name === "default_theme");
      if (defaultTheme) {
        setNewThemeId(defaultTheme.value);
      }
    }
  }, [isThemeInUse, setNewThemeId, themes]);

  if (isLoading) {
    return <LoaderRow />;
  }

  if (!themes || !themeStats) {
    return null;
  }

  return (
    <Modal.Body className="space-y-4 !overflow-visible pb-11 md:!px-8">
      {isThemeInUse ? (
        <>
          <ThemeUsageStats themeStats={themeStats} />
          <ThemeSelector
            appearance={appearance}
            currentCanvasTheme={currentCanvasTheme}
            themes={themes}
            newThemeId={newThemeId}
            onThemeChange={setNewThemeId}
          />
        </>
      ) : (
        <Typography.BodyMd color="text-dark">
          {t(
            "builder_ui.theme_configuration.delete_custom_theme_modal.description.theme_not_in_use",
          )}
        </Typography.BodyMd>
      )}
    </Modal.Body>
  );
};
